import { AddUserSchema, UpdateUserSchema } from '@buyerflo/core/schemas/user'
import { FrontendUserSchema } from '@buyerflo/core/schemas/userMembership'
import { Badge } from '@buyerflo/ui/components/Badge'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Clock, ShieldCheck, Wallet } from '@tamagui/lucide-icons-2'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useConjunctionalAgenciesApi, useRolesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import { SlotAutocomplete } from 'components/autocomplete'
import Checkbox from 'components/checkbox'
import { DeleteRounded, EditRoundedIcon, InfoOutlined } from 'components/icons'
import { SlotInput, SlotWrapper } from 'components/input'
import { Label } from 'components/label'
import SectionHead from 'components/sectionHead'
import Select, { Option } from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { formatDistanceToNow } from 'date-fns'
import { convertFileToBase64 } from 'helperFunctions/file'
import { prop } from 'ramda'
import { useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import Button from 'components/button'
import { SizableText, Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { useOptionalOnboarding } from 'onboarding/OnboardingContext'
import EditAvatarModal from '../editAvatarModal'
import RemoveUserModal from './removeUserModal'

const Stat = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
    <XStack
        alignItems="center"
        gap="$2.5"
        paddingVertical="$2"
        borderBottomWidth={1}
        borderBottomColor="$borderColor"
    >
        <View
            width={16}
            alignItems="center"
        >
            {icon}
        </View>
        <SizableText
            flex={1}
            size="$1"
            color="$colorMuted"
        >
            {label}
        </SizableText>
        <SizableText
            size="$2"
            fontWeight="500"
            fontFamily="$mono"
        >
            {value}
        </SizableText>
    </XStack>
)

export default function CoreEditUser({ user: editUser }: { user?: FrontendUserSchema }) {
    const trpc = useTRPC()
    const { user: userData } = useUserData()
    const navigate = useNavigate()
    const showSnack = useShowSnack()
    const onboarding = useOptionalOnboarding()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()
    const usersApi = useUsersApi()
    const rolesApi = useRolesApi()
    const updateMeMutation = useMutation(trpc.users.updateMe.mutationOptions())
    const updateUserMutation = useMutation(trpc.users.updateUser.mutationOptions())
    const addUserMutation = useMutation(trpc.users.addUser.mutationOptions())
    const createProfileImageUploadMutation = useMutation(
        trpc.users.createProfileImageUpload.mutationOptions(),
    )
    const setProfileImageMutation = useMutation(trpc.users.setProfileImage.mutationOptions())
    const uploadAgentAppointmentAttachmentMutation = useMutation(
        trpc.users.uploadAgentAppointmentAttachment.mutationOptions(),
    )
    const removeAgentAppointmentAttachmentMutation = useMutation(
        trpc.users.removeAgentAppointmentAttachment.mutationOptions(),
    )
    const billingQuery = useQuery(trpc.billing.getBilling.queryOptions())
    const getPortalMutation = useMutation(trpc.billing.getPortalUrl.mutationOptions())

    const inputRef = useRef<HTMLInputElement>(null)
    const emailSigInputRef = useRef<HTMLInputElement>(null)
    const agentAppointmentAttachmentInputRef = useRef<HTMLInputElement>(null)

    const [avatarImage, setAvatarImage] = useState<string | undefined>(undefined)
    const [forceRefreshProfileImage, setForceRefreshProfileImage] = useState(Math.random() * 1000)
    const [emailChanged, setEmailChanged] = useState(false)
    const [removeUserModal, setRemoveUserModal] = useState(false)
    const [agentAppointmentAttachment, setAgentAppointmentAttachment] = useState<
        { key: string; filename: string } | undefined
    >(editUser?.agentAppointmentAttachment)

    const { control, handleSubmit, formState, setValue, watch } = useForm<AddUserSchema>({
        defaultValues: {
            superUser: false,
            salesAgent: false,
            ...(editUser ?? {}),
        },
        resolver: zodResolver(editUser ? UpdateUserSchema : AddUserSchema),
    })

    const salesAgent = watch('salesAgent')
    const salesAssistantLeadAgentId = watch('salesAssistantLeadAgentId')
    const salesAssistantConjunctionalAgencyId = watch('salesAssistantConjunctionalAgencyId')

    const [salesAssistant, setSalesAssistant] = useState(
        editUser?.salesAssistantLeadAgentId != undefined ||
            editUser?.salesAssistantConjunctionalAgencyId != undefined,
    )

    if (Object.keys(formState.errors).length > 0) {
        console.log('form errors', formState.errors)
    }

    const superUser = watch('superUser')

    const [editAvatarImage, setEditAvatarImage] = useState<string | null>(null)

    const formDisabled = !userData.permissions.includes('users.edit')

    const onSubmit: SubmitHandler<UpdateUserSchema> = async (data: UpdateUserSchema) => {
        if (!emailChanged) {
            delete data.emailSignature
        }

        try {
            if (editUser?.userId == userData.userId) {
                await updateMeMutation.mutateAsync({
                    firstName: data.firstName,
                    lastName: data.lastName,
                })
            }
            if (editUser) {
                await updateUserMutation.mutateAsync({
                    userId: editUser.userId,
                    data,
                })
            } else {
                await addUserMutation.mutateAsync({
                    ...(data as any),
                    avatarImage,
                })
                navigate(`/${userData.orgId}/settings/users`)
            }
            usersApi.refresh()
            onboarding?.refetchProgress()
            showSnack(`User ${editUser ? 'updated' : 'added'}`, 'success')
        } catch (e) {
            showSnack(`Error ${editUser ? 'updating' : 'adding'} user`, 'danger')
        }
    }

    if (!editUser && billingQuery.isPending) {
        return <Spinner color="$primary" />
    }

    const hasNoSeats =
        !editUser &&
        billingQuery.data?.hasBilling &&
        billingQuery.data.maxSeats !== null &&
        billingQuery.data.maxSeats !== undefined &&
        billingQuery.data.currentSeatCount >= billingQuery.data.maxSeats

    if (hasNoSeats) {
        return (
            <YStack
                gap="$4"
                maxWidth={500}
                paddingVertical="$4"
            >
                <YStack
                    backgroundColor="$red3"
                    padding="$4"
                    borderRadius="$4"
                    gap="$3"
                >
                    <XStack
                        alignItems="center"
                        gap="$2"
                    >
                        <InfoOutlined size={20} />
                        <Text
                            fontWeight="600"
                            fontSize="$5"
                        >
                            No seats available
                        </Text>
                    </XStack>
                    <Text>
                        You've reached your seat limit of {billingQuery.data?.hasBilling && billingQuery.data.maxSeats} users. Update your
                        billing plan to add more users.
                    </Text>
                </YStack>
                <XStack gap="$3">
                    <Button
                        variant="outlined"
                        onPress={() => navigate(`/${userData.orgId}/settings/users`)}
                    >
                        Back to Users
                    </Button>
                    <Button
                        onPress={async () => {
                            const { url } = await getPortalMutation.mutateAsync()
                            window.location.href = url
                        }}
                    >
                        Manage Billing
                    </Button>
                </XStack>
            </YStack>
        )
    }

    const canEditThis = !formDisabled || userData.userId == editUser?.userId
    const nameDisabled = editUser != undefined && editUser?.userId != userData.userId

    const lastLoginQuery = useQuery({
        ...trpc.users.getUserLastLogin.queryOptions({ userId: editUser?.userId ?? '' }),
        enabled: editUser?.userId != undefined,
    })
    const lastLoggedIn = lastLoginQuery.data?.lastLoggedIn
    const lastLogin = lastLoginQuery.isPending
        ? '…'
        : lastLoggedIn != undefined && lastLoggedIn > 0
          ? formatDistanceToNow(new Date(lastLoggedIn), { addSuffix: true })
          : 'Never'
    const roleLabel = editUser?.superUser
        ? 'Super User'
        : rolesApi.data?.find(role => role.id === editUser?.roleId)?.roleName ?? '—'

    const isNew = !editUser
    const backToList = () => navigate(`/${userData.orgId}/settings/users`)
    const saveDisabled = formDisabled && editUser?.userId != userData.userId

    return (
        <>
            <SectionHead
                title={editUser ? `${editUser.firstName} ${editUser.lastName}` : 'Invite User'}
                subtitle={isNew ? 'Add a new member to your team.' : undefined}
                titleChip={
                    editUser ? (
                        <Badge
                            tone={editUser.active ? 'ok' : 'warn'}
                            dot
                        >
                            {editUser.active ? 'Active' : 'Pending invite'}
                        </Badge>
                    ) : undefined
                }
                back={{ label: 'Users', onPress: backToList }}
                buttons={
                    <XStack gap="$2">
                        <Button
                            variant="outlined"
                            borderRadius="$4"
                            onPress={backToList}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-tour="user-save-button"
                            // @ts-ignore
                            type="submit"
                            theme="primary"
                            fontWeight="600"
                            borderRadius="$4"
                            onPress={handleSubmit(onSubmit)}
                            disabled={saveDisabled}
                        >
                            {isNew ? 'Save' : 'Save changes'}
                        </Button>
                    </XStack>
                }
            />
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
            >
                <XStack
                    gap="$4"
                    flexDirection="column"
                    alignItems="stretch"
                    $md={{ flexDirection: 'row', alignItems: 'flex-start' }}
                    paddingBottom="$8"
                >
                    {/* Main column */}
                    <YStack
                        flex={1}
                        minWidth={0}
                        maxWidth={680}
                        gap="$4"
                    >
                        {/* Profile */}
                        <FormSection
                            title="Profile"
                            description="Name, contact, and photo"
                        >
                            <XStack
                                gap="$4"
                                alignItems="center"
                                paddingBottom="$3.5"
                                borderBottomWidth={1}
                                borderBottomColor="$borderColor"
                            >
                                <View
                                    width={72}
                                    height={72}
                                    position="relative"
                                >
                                    <View
                                        width={72}
                                        height={72}
                                        borderRadius={36}
                                        overflow="hidden"
                                        backgroundColor="$primaryMuted"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        {editUser?.profileImage ? (
                                            <img
                                                src={`${editUser.profileImage}?forceRefreshProfileImage=${forceRefreshProfileImage}`}
                                                loading="lazy"
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <SizableText
                                                fontSize={24}
                                                fontWeight="600"
                                            >
                                                {`${editUser?.firstName?.[0] ?? ''}${editUser?.lastName?.[0] ?? ''}`}
                                            </SizableText>
                                        )}
                                    </View>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={inputRef}
                                        onChange={e => {
                                            if (e?.target?.files && e.target.files[0]) {
                                                setEditAvatarImage(URL.createObjectURL(e.target.files[0]))
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={emailSigInputRef}
                                        onChange={async e => {
                                            if (e?.target?.files && e.target.files[0]) {
                                                const b64 = await convertFileToBase64(e.target.files[0])
                                                setValue('emailSignature', b64)
                                                setEmailChanged(true)
                                            }
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                    {canEditThis && (
                                        <Button
                                            size="$2"
                                            variant="outlined"
                                            circular
                                            position="absolute"
                                            zIndex={2}
                                            bottom={-4}
                                            right={-4}
                                            onPress={e => {
                                                e.preventDefault()
                                                inputRef.current!.click()
                                            }}
                                        >
                                            <EditRoundedIcon size={14} />
                                        </Button>
                                    )}
                                </View>
                                <YStack
                                    flex={1}
                                    minWidth={0}
                                    gap="$1"
                                >
                                    <SizableText
                                        size="$3"
                                        fontWeight="600"
                                    >
                                        {editUser ? `${editUser.firstName} ${editUser.lastName}` : 'New user'}
                                    </SizableText>
                                    <SizableText
                                        size="$1"
                                        color="$colorMuted"
                                    >
                                        JPG or PNG · click the pencil to change
                                    </SizableText>
                                </YStack>
                            </XStack>

                            <XStack
                                gap="$3"
                                flexWrap="wrap"
                            >
                                <View
                                    flex={1}
                                    minWidth={160}
                                >
                                    <Controller
                                        name="firstName"
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="First Name"
                                                disabled={nameDisabled}
                                                {...field}
                                            />
                                        )}
                                    />
                                </View>
                                <View
                                    flex={1}
                                    minWidth={160}
                                >
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Last Name"
                                                disabled={nameDisabled}
                                                {...field}
                                            />
                                        )}
                                    />
                                </View>
                            </XStack>
                            <XStack
                                gap="$3"
                                flexWrap="wrap"
                            >
                                <View
                                    flex={1}
                                    minWidth={160}
                                >
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Email"
                                                {...field}
                                                disabled={editUser != undefined}
                                            />
                                        )}
                                    />
                                </View>
                                <View
                                    flex={1}
                                    minWidth={160}
                                >
                                    <Controller
                                        name="mobileNumber"
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Mobile Number"
                                                disabled={editUser != undefined}
                                                {...field}
                                            />
                                        )}
                                    />
                                </View>
                            </XStack>
                        </FormSection>

                        {/* Role */}
                        <FormSection
                            title="Role"
                            description="Control what this user can see and do"
                        >
                            {!superUser && (
                                <Controller
                                    name="roleId"
                                    control={control}
                                    render={field => (
                                        <SlotWrapper
                                            label="Primary Role"
                                            {...field}
                                        >
                                            <Select
                                                value={field.field.value}
                                                disabled={!userData.permissions.includes('users.edit')}
                                                onChange={(_e: any, value: any) => setValue('roleId', value)}
                                            >
                                                {rolesApi.data?.map(role => (
                                                    <Option
                                                        key={role.id}
                                                        value={role.id}
                                                    >
                                                        {role.roleName}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </SlotWrapper>
                                    )}
                                />
                            )}

                            <YStack
                                gap="$2.5"
                                paddingTop="$1"
                            >
                                <SizableText
                                    fontSize={11}
                                    fontWeight="500"
                                    letterSpacing={0.4}
                                    textTransform="uppercase"
                                    color="$colorMuted"
                                >
                                    Additional capabilities
                                </SizableText>
                                <Controller
                                    name="superUser"
                                    control={control}
                                    render={field => (
                                        <Checkbox
                                            label="Super User"
                                            disabled={!userData.superUser}
                                            onChange={e => field.field.onChange(e.target.checked)}
                                            checked={field.field.value}
                                        />
                                    )}
                                />
                                <Controller
                                    name="salesAgent"
                                    control={control}
                                    render={field => (
                                        <Checkbox
                                            label="Sales Agent"
                                            disabled={formDisabled}
                                            onChange={e => field.field.onChange(e.target.checked)}
                                            checked={field.field.value}
                                        />
                                    )}
                                />
                                <Checkbox
                                    label="Sales Assistant"
                                    disabled={formDisabled}
                                    onChange={e => {
                                        setSalesAssistant(e.target.checked)
                                        if (!e.target.checked) {
                                            setValue('salesAssistantLeadAgentId', null)
                                            setValue('salesAssistantConjunctionalAgencyId', null)
                                        }
                                    }}
                                    checked={salesAssistant}
                                />
                                {salesAssistant && (
                                    <YStack
                                        gap="$1.5"
                                        paddingLeft="$6"
                                    >
                                        <Label>Assistant For</Label>
                                        {/* Using a native HTML select for the complex grouped autocomplete */}
                                        <select
                                            disabled={formDisabled}
                                            onChange={event => {
                                                const val = event.target.value
                                                if (!val) {
                                                    setValue('salesAssistantLeadAgentId', null)
                                                    setValue('salesAssistantConjunctionalAgencyId', null)
                                                    return
                                                }
                                                const [type, id] = val.split('::')
                                                if (type === 'user') {
                                                    setValue('salesAssistantLeadAgentId', id)
                                                    setValue('salesAssistantConjunctionalAgencyId', null)
                                                } else {
                                                    setValue('salesAssistantConjunctionalAgencyId', id)
                                                    setValue('salesAssistantLeadAgentId', null)
                                                }
                                            }}
                                            value={
                                                salesAssistantLeadAgentId
                                                    ? `user::${salesAssistantLeadAgentId}`
                                                    : salesAssistantConjunctionalAgencyId
                                                      ? `agency::${salesAssistantConjunctionalAgencyId}`
                                                      : ''
                                            }
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: '1px solid var(--borderColor)',
                                                backgroundColor: 'transparent',
                                                fontSize: '14px',
                                            }}
                                        >
                                            <option value="">Select...</option>
                                            <optgroup label="Agencies">
                                                {(conjunctionalAgenciesApi.data ?? []).map((x: any) => (
                                                    <option
                                                        key={x.conjunctionalAgencyId}
                                                        value={`agency::${x.conjunctionalAgencyId}`}
                                                    >
                                                        {x.licenseeName}
                                                    </option>
                                                ))}
                                            </optgroup>
                                            <optgroup label="Users">
                                                {(usersApi.data ?? []).map((x: any) => (
                                                    <option
                                                        key={x.userId}
                                                        value={`user::${x.userId}`}
                                                    >
                                                        {x.firstName} {x.lastName}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        </select>
                                    </YStack>
                                )}
                            </YStack>
                        </FormSection>

                        {/* Commission */}
                        {salesAgent && (
                            <FormSection
                                title="Commission"
                                description="How this agent is paid out"
                            >
                                <Controller
                                    name="conjunctionalAgencyId"
                                    control={control}
                                    disabled={formDisabled}
                                    render={field => (
                                        <SlotAutocomplete
                                            {...field}
                                            label="Conjunctional Agency"
                                            onChange={(_event, value) => field.field.onChange(value)}
                                            options={(conjunctionalAgenciesApi.data ?? []).map(
                                                prop('conjunctionalAgencyId'),
                                            )}
                                            getOptionLabel={agencyId =>
                                                (conjunctionalAgenciesApi.data ?? []).find(
                                                    agency => agency.conjunctionalAgencyId === agencyId,
                                                )?.licenseeName ?? 'loading...'
                                            }
                                        />
                                    )}
                                />
                                <View data-tour="user-engagement-fee">
                                    <Controller
                                        name="engagementFee"
                                        control={control}
                                        render={field => (
                                            <SlotInput
                                                label="Engagement Fee"
                                                type="dollar"
                                                disabled={formDisabled}
                                                {...field}
                                            />
                                        )}
                                    />
                                </View>
                            </FormSection>
                        )}

                        {/* Agent Appointment Attachment */}
                        {editUser && (
                            <FormSection
                                title="Agent Appointment Attachment"
                                description="Optional PDF automatically attached when an Agent Appointment Form is created for this user."
                            >
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    ref={agentAppointmentAttachmentInputRef}
                                    onChange={async e => {
                                        const file = e?.target?.files?.[0]
                                        if (!file) return
                                        try {
                                            const dataUrl = await convertFileToBase64(file)
                                            const result =
                                                await uploadAgentAppointmentAttachmentMutation.mutateAsync({
                                                    userId: editUser.userId,
                                                    filename: file.name,
                                                    base64String: dataUrl,
                                                })
                                            setAgentAppointmentAttachment(result)
                                            await usersApi.refresh()
                                            showSnack('Attachment uploaded', 'success')
                                        } catch (err) {
                                            showSnack('Error uploading attachment', 'danger')
                                        } finally {
                                            if (agentAppointmentAttachmentInputRef.current) {
                                                agentAppointmentAttachmentInputRef.current.value = ''
                                            }
                                        }
                                    }}
                                    style={{ display: 'none' }}
                                />
                                {agentAppointmentAttachment ? (
                                    <XStack
                                        alignItems="center"
                                        gap="$3"
                                        flexWrap="wrap"
                                    >
                                        <Text flex={1}>{agentAppointmentAttachment.filename}</Text>
                                        <Button
                                            variant="outlined"
                                            disabled={formDisabled}
                                            onPress={() => {
                                                agentAppointmentAttachmentInputRef.current!.click()
                                            }}
                                        >
                                            Replace
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            theme="red"
                                            disabled={formDisabled}
                                            onPress={async () => {
                                                try {
                                                    await removeAgentAppointmentAttachmentMutation.mutateAsync(
                                                        { userId: editUser.userId },
                                                    )
                                                    setAgentAppointmentAttachment(undefined)
                                                    await usersApi.refresh()
                                                    showSnack('Attachment removed', 'success')
                                                } catch {
                                                    showSnack('Error removing attachment', 'danger')
                                                }
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </XStack>
                                ) : (
                                    <View>
                                        <Button
                                            variant="outlined"
                                            disabled={formDisabled}
                                            onPress={() => {
                                                agentAppointmentAttachmentInputRef.current!.click()
                                            }}
                                        >
                                            Upload PDF
                                        </Button>
                                    </View>
                                )}
                            </FormSection>
                        )}
                    </YStack>

                    {/* Side rail */}
                    <YStack
                        width="100%"
                        gap="$4"
                        $md={{ width: 320, position: 'sticky', top: 12 }}
                    >
                        {editUser && (
                            <FormSection title="At a glance">
                                <YStack>
                                    <Stat
                                        icon={<ShieldCheck size={14} color="$colorMuted" />}
                                        label="Role"
                                        value={roleLabel}
                                    />
                                    {salesAgent && (
                                        <Stat
                                            icon={<Wallet size={14} color="$colorMuted" />}
                                            label="Engagement fee"
                                            value={
                                                editUser.engagementFee != undefined
                                                    ? `$${editUser.engagementFee}`
                                                    : '—'
                                            }
                                        />
                                    )}
                                    <Stat
                                        icon={<Building2 size={14} color="$colorMuted" />}
                                        label="Status"
                                        value={editUser.active ? 'Active' : 'Pending'}
                                    />
                                    <Stat
                                        icon={<Clock size={14} color="$colorMuted" />}
                                        label="Last login"
                                        value={lastLogin}
                                    />
                                </YStack>
                            </FormSection>
                        )}

                        {editUser &&
                            userData.permissions.includes('users.delete') &&
                            editUser.userId !== userData.userId && (
                                <FormSection
                                    title="Danger zone"
                                    description="Removing a user revokes their access immediately."
                                >
                                    <Button
                                        variant="outlined"
                                        theme="red"
                                        icon={<DeleteRounded size={15} />}
                                        alignSelf="stretch"
                                        onPress={() => setRemoveUserModal(true)}
                                    >
                                        Remove User
                                    </Button>
                                </FormSection>
                            )}
                    </YStack>
                </XStack>
            </form>
            <EditAvatarModal
                image={editAvatarImage}
                name={editUser ? `${editUser.firstName} ${editUser.lastName}` : undefined}
                onClose={() => setEditAvatarImage(null)}
                onSave={async avatarImage => {
                    if (editUser) {
                        try {
                            const { uploadUrl, profileImageId } =
                                await createProfileImageUploadMutation.mutateAsync()
                            const blob = await (await fetch(avatarImage)).blob()
                            const uploadResponse = await fetch(uploadUrl, {
                                method: 'PUT',
                                body: blob,
                                headers: { 'Content-Type': 'image/png' },
                            })
                            if (!uploadResponse.ok) {
                                throw new Error(`Upload failed (${uploadResponse.status})`)
                            }
                            await setProfileImageMutation.mutateAsync({
                                userId: editUser.userId,
                                profileImageId,
                            })
                            await usersApi.refresh()
                            setForceRefreshProfileImage(forceRefreshProfileImage + 1)
                        } catch (e) {
                            showSnack('Error uploading photo', 'danger')
                            return
                        }
                    } else {
                        setAvatarImage(avatarImage)
                    }
                    setEditAvatarImage(null)
                }}
            />
            <RemoveUserModal
                open={removeUserModal}
                user={editUser}
                onClose={() => setRemoveUserModal(false)}
            />
        </>
    )
}
